import React, { MouseEvent, PureComponent } from 'react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { scroller } from 'react-scroll';
import styled, { ThemeProps } from 'styled-components';
import {
  Contact,
  ContactAdress,
  ContactItem,
  ContactTextItem
} from '../../../components/Contact';
import { FontIcon } from '../../../components/FontIcon';
import { Image } from '../../../components/Image';
import { HtmlContent } from '../../../components/layout/HtmlContent';
import { Anker } from '../../../components/Link';
import { Section } from '../../../components/Section';
import { Title } from '../../../components/Title';
import { TypoImage } from '../../../components/TypoImage';
import { scrollOptions } from '../../../configs';
import {
  withSearch,
  WithSearchProps
} from '../../../provider/SearchProvider/withSearch';
import {
  selectStage,
  selectVehicleCenters
} from '../../../provider/Typo3Provider/selectors';
import { Typo3State } from '../../../provider/Typo3Provider/types';
import Typo3Label from '../../../provider/Typo3Provider/Typo3Label';
import { withTypo3 } from '../../../provider/Typo3Provider/withTypo3';
import { Theme } from '../../../theme';
import { media } from '../../../theme/base/mixins';
import { StyledComponent, StyledDivComponent } from '../../../types';

const FontIconItem = FontIcon.withComponent(ContactItem);
const FontIconAnchor = FontIcon.withComponent(Link);

const CountryKeyvisualContainer = styled.div`
  position: relative;
  overflow: hidden;

  ${media.medium`
    min-height: 450px;
  `}

  ${media.large`
    min-height: 600px;
  `}

  ${Image} {
    position: absolute;
    min-height: 100%;
    width: auto;
    transform: translateX(-50%);
    left: 50%;
  }
`;

const CountryKeyvisualInfo: StyledComponent = Section.extend`
  padding: ${(props: ThemeProps<Theme>) => props.theme.grid.columnGap}px;

  ${media.medium`
    top: 0;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    padding-bottom: 0;
  `};
`;

const InfoBox: StyledDivComponent = styled.div`
  background: ${(props: ThemeProps<Theme>) => props.theme.colors.white};
  padding: 40px 30px;
  border-radius: 2px;
  white-space: nowrap;

  ${media.medium`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  `}

  ${Title.H3} {
    line-height: 1;
    font-size: 3rem;
    font-style: normal;
    font-weight: bold;
    text-align: left;
    white-space: normal;

    ${media.large`
      font-weight: 300;
    `};
  }

  ${Title.H3}, ${Contact} {
    padding-bottom: 10px;
    margin-bottom: 10px;
    border-bottom: 1px solid
      ${(props: ThemeProps<Theme>) => props.theme.colors.primary};
  }

  ${ContactTextItem} {
    font-size: 2.4rem;
    font-weight: 300;
    line-height: 1.25;
    color: ${(props: ThemeProps<Theme>) => props.theme.colors.black};

    ${Anker} {
      font-size: 2.4rem;
      font-weight: inherit;
      color: inherit;
    }
  }

  ${FontIconAnchor}, ${FontIconItem} {
    font-size: 2.4rem;

    &:before {
      font-size: 2.4rem;
      line-height: 1.25;
      vertical-align: top;
      color: ${(props: ThemeProps<Theme>) => props.theme.colors.primary};
      margin-right: 10px;
    }
  }

  @media print {
    width: 100%;
    position: relative;
  }
`;

const LinkList: StyledDivComponent = styled.div`
  ${FontIconAnchor} {
    display: block;
    margin-bottom: 5px;
    color: ${(props: ThemeProps<Theme>) => props.theme.colors.primary};
    text-decoration: none;
    line-height: 1.25;
    font-weight: 500;

    &:before {
      vertical-align: bottom;
    }
  }
`;

type CountryKeyvisualProps = RouteComponentProps &
  WithSearchProps & {
    typo3: Typo3State;
  };

interface CountryKeyvisualState {
  busses: boolean;
}

class CountryKeyvisual extends PureComponent<
  CountryKeyvisualProps,
  CountryKeyvisualState
> {
  public state: CountryKeyvisualState = {
    busses: false
  };

  public async componentDidMount() {
    const { search, typo3 } = this.props;
    const vehicleCenters = selectVehicleCenters(typo3);
    const usedvehiclecenter = vehicleCenters.find(
      center => center.link === window.location.pathname
    );

    if (usedvehiclecenter) {
      const offers = await search.getOffersByLocation(
        search.formParams,
        usedvehiclecenter.searchValue
      );

      this.setState({
        busses: !!offers.length
      });
    }
  }

  public scrollTo = (e: MouseEvent, el: string) => {
    e.preventDefault();
    scroller.scrollTo(el, scrollOptions);
  };

  public render() {
    const { location, typo3 } = this.props;
    const { busses } = this.state;
    const items = selectStage(typo3);
    const vehicleCenters = selectVehicleCenters(typo3);
    const [keyvisual] = items;
    const images =
      keyvisual && keyvisual.files && keyvisual.files.image
        ? keyvisual.files.image
        : [];
    const [image] = images;

    const usedvehiclecenter = vehicleCenters.find(
      center => center.link === location.pathname
    );

    return (
      <CountryKeyvisualContainer>
        {image && (
          <TypoImage
            image={image}
            alt={keyvisual ? keyvisual.header : undefined}
            className="hide-for-print"
          />
        )}

        {usedvehiclecenter && (
          <CountryKeyvisualInfo>
            <InfoBox>
              <Title.H3>
                <HtmlContent htmlString={usedvehiclecenter.name} />
              </Title.H3>

              <Contact>
                <FontIconItem show icon="position">
                  <ContactAdress>
                    <ContactTextItem>
                      {usedvehiclecenter.company}
                    </ContactTextItem>
                    <ContactTextItem>
                      {usedvehiclecenter.street}
                    </ContactTextItem>
                    <ContactTextItem>
                      {usedvehiclecenter.zip} {usedvehiclecenter.city}
                    </ContactTextItem>
                  </ContactAdress>
                </FontIconItem>
              </Contact>

              <LinkList>
                <FontIconAnchor
                  to=""
                  icon="user"
                  onClick={(e: MouseEvent) => this.scrollTo(e, 'contacts')}
                >
                  <Typo3Label label="contact.contactpersons">
                    Ansprechpartner
                  </Typo3Label>
                </FontIconAnchor>
                {busses && (
                  <FontIconAnchor
                    to=""
                    icon="vehicle"
                    onClick={(e: MouseEvent) => this.scrollTo(e, 'busses')}
                  >
                    <Typo3Label label="contact.vehicles">
                      Fahrzeuge
                    </Typo3Label>
                  </FontIconAnchor>
                )}
                <FontIconAnchor
                  to=""
                  icon="clock"
                  onClick={(e: MouseEvent) => this.scrollTo(e, 'approach')}
                >
                  <Typo3Label label="contact.openinghours">
                    Öffnungszeiten
                  </Typo3Label>
                </FontIconAnchor>
              </LinkList>
            </InfoBox>
          </CountryKeyvisualInfo>
        )}
      </CountryKeyvisualContainer>
    );
  }
}

export default withRouter(withTypo3(withSearch(CountryKeyvisual)));
